import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormInput } from 'Shared/components/FormInput';
import { RadioGroup } from 'Shared/components/RadioGroup';
import { ATTRIBUTE_TYPES, REASONS } from 'BlockList/constants';
import type { AttributeTypeMetadata } from 'Shared/services/Reznor/GetAttributeTypes';
import type {
  AttributeTypeKey,
  ReasonKey,
  BlockListStatusFilter,
} from 'BlockList/types';
import styles from './BlockListFilters.module.css';

export type BlockListFiltersState = {
  q: string;
  // Empty string represents "no filter applied".
  attributeType: AttributeTypeKey | '';
  reason: ReasonKey | '';
  status: BlockListStatusFilter;
};

type BlockListFiltersProps = {
  filters: BlockListFiltersState;
  onChange: (filters: BlockListFiltersState) => void;
  // Attribute-type catalog fetched once by the page shell. Drives the
  // Attribute Type dropdown; empty falls back to the local constant.
  attributeTypes?: AttributeTypeMetadata[];
};

const STATUS_OPTIONS: { label: string; value: BlockListStatusFilter }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

const DEBOUNCE_MS = 300;

// Build a multiselect items array out of an enum-like object, marking
// the currently-selected key (or none).
const toItems = (
  entries: Record<string, { label: string }>,
  selectedKey: string
) =>
  Object.entries(entries).map(([key, { label }]) => ({
    key,
    label,
    selected: selectedKey === key,
  }));

export const BlockListFilters: React.FC<BlockListFiltersProps> = ({
  filters,
  onChange,
  attributeTypes = [],
}) => {
  // Narrow the Attribute Type dropdown to the set the server reports; fall
  // back to the local constant while loading or on failure so the filter is
  // never empty. Mirrors the AddAttributeModal pattern.
  const attributeTypeOptions =
    attributeTypes.length > 0
      ? attributeTypes.reduce<Record<string, { label: string }>>((acc, t) => {
          const label = ATTRIBUTE_TYPES[t.attributeType]?.label;
          if (label) acc[t.attributeType] = { label };
          return acc;
        }, {})
      : ATTRIBUTE_TYPES;

  const [searchInput, setSearchInput] = useState(filters.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep a ref to the latest filters so the debounced setTimeout closure
  // doesn't merge over a stale snapshot. Without this, a fast keystroke
  // followed by an Attribute Type / Reason / Status change before 300ms
  // would overwrite the newer non-search filter.
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Sync search input if filters.q is reset externally (e.g. filter clear)
  useEffect(() => {
    setSearchInput(filters.q);
  }, [filters.q]);

  // Cancel any in-flight debounce when the component unmounts so a
  // post-unmount setTimeout callback can't fire onChange on a stale
  // parent.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const trimmed = value.trim();
        if (trimmed !== filtersRef.current.q) {
          onChange({ ...filtersRef.current, q: trimmed });
        }
      }, DEBOUNCE_MS);
    },
    [onChange]
  );

  // Single-selection toggle on top of the multiselect contract:
  // clicking the currently-selected item clears the filter; clicking
  // any other item replaces the single value.
  const handleAttributeTypeChange = useCallback(
    (item: { key: string; selected: boolean }) => {
      onChange({
        ...filters,
        attributeType: item.selected ? '' : (item.key as AttributeTypeKey),
      });
    },
    [filters, onChange]
  );

  const handleReasonChange = useCallback(
    (item: { key: string; selected: boolean }) => {
      onChange({
        ...filters,
        reason: item.selected ? '' : (item.key as ReasonKey),
      });
    },
    [filters, onChange]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      onChange({ ...filters, status: value as BlockListStatusFilter });
    },
    [filters, onChange]
  );

  return (
    <>
      <div className={styles.searchRow}>
        <FormInput
          variant="search"
          name="search"
          label="Search by attribute or policy number"
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleSearchChange(e.target.value)
          }
        />
      </div>
      <div className={styles.filtersRow}>
        <FormInput
          variant="multiselect"
          label="Attribute Type"
          items={toItems(attributeTypeOptions, filters.attributeType)}
          onChangeSelection={handleAttributeTypeChange}
        />
        <FormInput
          variant="multiselect"
          label="Reason"
          items={toItems(REASONS, filters.reason)}
          onChangeSelection={handleReasonChange}
        />
        <RadioGroup
          className={styles.statusRadio}
          name="status"
          variant="inline"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={handleStatusChange}
        />
      </div>
    </>
  );
};
